/**
 * Plant Status Banner Component
 * Displays dynamic status banners based on SCADA tag values
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  keyframes,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { StatusBanner, BannerStatus } from '../../types/banners';

// Use types from the central types file

interface PlantStatusBannerProps {
  banners: StatusBanner[];
  maxHeight?: number;
}

// Animation keyframes
const pulseAnimation = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.02); }
  100% { opacity: 1; transform: scale(1); }
`;

const blinkAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const stripedAnimation = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 40px 0; }
`;

export const PlantStatusBanner: React.FC<PlantStatusBannerProps> = ({
  banners,
  maxHeight = 200,
}) => {
  // Filter and sort active banners with current status
  const activeBanners = banners
    .filter(banner => banner.is_active && banner.current_status)
    .sort((a, b) => a.priority - b.priority);

  if (activeBanners.length === 0) {
    return null;
  }

  // Calculate how many banners we can show within maxHeight
  let totalHeight = 0;
  const visibleBanners = [];
  
  for (const banner of activeBanners) {
    const bannerHeight = banner.height_pixels + 8; // Add margin
    if (totalHeight + bannerHeight <= maxHeight) {
      visibleBanners.push(banner);
      totalHeight += bannerHeight;
    } else {
      break; // Don't show more banners if they exceed maxHeight
    }
  }

  const getBannerStyles = (status: BannerStatus, height: number) => {
    const baseStyles: any = {
      height: `${height}px`,
      color: status.text_color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      borderRadius: '4px',
      marginBottom: '4px',
      fontWeight: 'bold',
      position: 'relative',
      overflow: 'hidden',
    };

    // Background styling based on banner_style
    switch (status.banner_style) {
      case 'solid':
        baseStyles.backgroundColor = status.background_color;
        break;
        
      case 'gradient':
        const endColor = status.gradient_end_color || status.background_color;
        baseStyles.background = `linear-gradient(135deg, ${status.background_color} 0%, ${endColor} 100%)`;
        break;
        
      case 'striped':
        baseStyles.background = `repeating-linear-gradient(
          45deg,
          ${status.background_color},
          ${status.background_color} 10px,
          ${status.text_color}20 10px,
          ${status.text_color}20 20px
        )`;
        if (status.animation_enabled) {
          baseStyles.animation = `${stripedAnimation} 1s linear infinite`;
        }
        break;
        
      case 'pulse':
        baseStyles.backgroundColor = status.background_color;
        if (status.animation_enabled) {
          baseStyles.animation = `${pulseAnimation} 2s ease-in-out infinite`;
        }
        break;
        
      case 'blink':
        baseStyles.backgroundColor = status.background_color;
        if (status.animation_enabled) {
          baseStyles.animation = `${blinkAnimation} 1s ease-in-out infinite`;
        }
        break;
        
      default:
        baseStyles.backgroundColor = status.background_color;
    }

    return baseStyles;
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxHeight: `${maxHeight}px`,
        mb: 1,
      }}
    >
      {visibleBanners.map((banner) => {
        const status = banner.current_status!;
        
        return (
          <Paper
            key={banner.id}
            elevation={2}
            sx={getBannerStyles(status, banner.height_pixels)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: banner.height_pixels > 40 ? '1.1rem' : '0.9rem',
                  fontWeight: 'bold',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  textAlign: 'center',
                }}
              >
                {status.banner_text}
              </Typography>
            </Box>
            
            {status.show_timestamp && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: '0.9rem', opacity: 0.8 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    opacity: 0.9,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {new Date().toLocaleTimeString()}
                </Typography>
              </Box>
            )}
          </Paper>
        );
      })}
      
      {/* Show indicator if there are more banners that couldn't fit */}
      {activeBanners.length > visibleBanners.length && (
        <Box
          sx={{
            textAlign: 'center',
            py: 0.5,
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          +{activeBanners.length - visibleBanners.length} more status{activeBanners.length - visibleBanners.length === 1 ? '' : 'es'}
        </Box>
      )}
    </Box>
  );
};
